import React, { useEffect, useRef, useState } from 'react';
import { orderBy, SortDescriptor } from '@progress/kendo-data-query';
import useTranslate from '../../../../../common/hooks/useTranslate';
import useGetGridData from './useGetGridData';

import { IWidget } from '../../../types/IWidget';

import { Grid, GridColumn, GridSortChangeEvent } from '@progress/kendo-react-grid';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import SwitchButtons from '../../../../ui/button/switchButtons/switchButtons';
import WidgetTitle from '../widgetTitle/widgetTitle';
import CashflowCell from '../cashflow/cells/cashflowCell/cashflowCell';
import TableLoading from '../../../../ui/loaders/tableLoader/tableLoading';
import GridFooterCell from './cell/gridFooterCell/gridFooterCell';
import TextCell from '../../../../ui/table/cells/textCell/textCell';
import StaticPropertyCell from './cell/staticPropertyCell/staticPropertyCell';
import OutletLoader from '../../../../ui/loaders/outletLoader/outletLoader';

import './gridWidget.scss';
import { useInternationalization } from '@progress/kendo-react-intl';

const GridWidget: React.FC<IWidget> = ({
                                         setup,
                                         widget,
                                         measures,
                                         onExpand,
                                         onCollapse,
                                         expand,
                                         notes,
                                         scenarios,
                                         boardId,
                                         id,
                                         staticProperties,
                                         handleWidgetMove,
                                         handleWidgetChange,
                                         changeNewIndex,
                                         widgets,
                                         changeOldIndex,
                                         customisation,
                                         selectedPeriod,
                                         selectedObject,
                                         boardSelectedCurrency,
                                         investorObjectId,
                                         homePage,
                                         handleUnpin,
                                         handlePinWidget,
                                         handleUnpinWidget,
                                         unpinId,
                                       }) => {
  const [sort, setSort] = useState<SortDescriptor[]>([{ field: 'name', dir: 'asc' }]);
  const [isMobile, setIsMobile] = useState<boolean>(false);

  const { t } = useTranslate();
  const tableRef = useRef(null);
  const intl = useInternationalization();

  const {
    sectionsForGrid,
    gridContentEnriched,
    preparedData,
    loading,
    gridNotes,
    measureHash,
    staticPropertyHash,
    objectValueMap,
    gridExport,
    exportGrid,
    selectGridSection,
    currencies,
    selectedCurrency,
    handleCurrencyChange,
  } = useGetGridData(setup, widget, measures, notes, staticProperties, scenarios, selectedObject, selectedPeriod, boardSelectedCurrency, investorObjectId);

  const createCFCellGrid = (columnName) => {
    return (props) => <CashflowCell {...props} columnName={columnName} hash={measureHash}
                                    objectValueMap={objectValueMap} boardId={boardId}
                                    selectedCurrency={selectedCurrency} investorObjectId={investorObjectId} singlePeriod={setup.singlePeriod} />;
  };

  const createFooterCellGrid = (gridData, name, totalCount) => {
    if (totalCount)
      return (props) => <GridFooterCell {...props} gridData={gridData} name={name} includeTotalCount={totalCount} />;
  };

  const createStaticPropertyCell = (column) => {
    return (props) => <StaticPropertyCell {...props} staticProperty={staticPropertyHash[column.templatePropertyId]} />;
  };

  const createTextCellGrid = () => {
    return (props) => <TextCell {...props} grid />;
  };

  const checkIfLocalIsSelected = (currency) => {
    return currency.currency === '_';
  };

  const numberCell = (column) => {
    return (props) => (
      <td style={{ textAlign: 'right' }}>
        {intl.toString(props.dataItem[props.field], `${column.format}${column.scale}`)}
      </td>
    )
  };

  useEffect(() => {
    let isMobile = window.innerWidth;
    if (isMobile < 960) {
      setIsMobile(true);
    }
  }, []);

  return loading ? (
    <OutletLoader />
  ) : (
    <>
      <WidgetTitle
        onClick={gridExport}
        currencySelection={(widget.pinned === null || widget.setup.currencySelection) ? currencies : false}
        selectedCurrency={selectedCurrency}
        handleCurrencyChange={handleCurrencyChange}
        name={widget.name}
        type={widget.type}
        onExpand={onExpand}
        onCollapse={onCollapse}
        expand={expand}
        notes={gridNotes}
        id={id}
        customisation={customisation}
        widgets={widgets}
        handleWidgetChange={handleWidgetChange}
        handleWidgetMove={handleWidgetMove}
        changeNewIndex={changeNewIndex}
        changeOldIndex={changeOldIndex}
        widget={widget}
        selectedObject={selectedObject}
        homePage={homePage}
        investorId={investorObjectId}
        handleUnpin={handleUnpin}
        handlePinWidget={handlePinWidget}
        handleUnpinWidget={handleUnpinWidget}
        unpinId={unpinId}
      />

      <div className='grid-widget-table'>
        <SwitchButtons buttons={sectionsForGrid} onClick={selectGridSection} />
        <div className='grid-widget-table-wrapper'>
          {!setup.singlePeriod &&
          <ExcelExport data={preparedData} ref={exportGrid}>
            <Grid data={orderBy(preparedData, sort)}
                  ref={tableRef}
                  sortable={true}
                  sort={sort}
                  scrollable={'scrollable'}
                  onSortChange={(sortEvent: GridSortChangeEvent) => {
                    setSort(sortEvent.sort);
                  }}>
              <GridColumn field='name' title={t('WIDGET.GRID_TITLE')} width={175} cell={TextCell} locked={!isMobile} />
              {gridContentEnriched.map((column, key) => {
                if (column.isTotal)
                  return (
                    <GridColumn title={column.name}
                                key={`top_${key}`}>
                      <GridColumn
                        field={`${column.type}_${column.measureId}`}
                        title={t('WIDGET.TOTAL')}
                        width={225}
                        key={key}
                        cell={numberCell(column)}
                      />
                    </GridColumn>
                  )
                return (
                  <GridColumn title={column.name}
                              key={`top_${key}`}>
                    <GridColumn
                      field={`${column.type}_${column.measureId}_${column.nodeId}`}
                      title={column.nodeName}
                      width={225}
                      key={key}
                      cell={column.viewCashflows ? createCFCellGrid(column.name) : createTextCellGrid()}
                    />
                  </GridColumn>
                )
              })}
            </Grid>
          </ExcelExport>
          }
          {setup.singlePeriod &&
          <ExcelExport data={preparedData} ref={exportGrid}>
            <Grid
              data={orderBy(preparedData, sort)}
              ref={tableRef}
              sortable={true}
              sort={sort}
              scrollable={'scrollable'}
              onSortChange={(sortEvent: GridSortChangeEvent) => {
                setSort(sortEvent.sort);
              }}>
              <GridColumn field='name' title={t('WIDGET.GRID_TITLE')} width={300} cell={TextCell} locked={!isMobile} />
              {checkIfLocalIsSelected(selectedCurrency) &&
                <GridColumn field='currency' title={t('WIDGET.GRID_CURRENCY')} width={100} />}
              {gridContentEnriched.map((column, key) => {
                if (column.type === 'property') {
                  return (
                    <GridColumn
                      field={`${column.type}_${column.templatePropertyId}`}
                      title={column.name}
                      width={225}
                      key={key}
                      cell={createStaticPropertyCell(column)}
                    />
                  );
                }
                return column.viewCashflows ? (
                  <GridColumn
                    field={`${column.type}_${column.measureId}`}
                    title={column.name}
                    width={225}
                    key={key}
                    cell={createCFCellGrid(column.name)}
                    footerCell={createFooterCellGrid(preparedData, column.name, column.total)}
                  />
                ) : (
                  <GridColumn
                    className='alignment-center'
                    field={`${column.type}_${column.measureId}`}
                    title={column.name}
                    width={225}
                    key={key}
                    cell={createTextCellGrid()}
                    footerCell={createFooterCellGrid(preparedData, column.name, column.total)}
                  />
                );
              })}
            </Grid>

          </ExcelExport>}
        </div>
        {loading && <TableLoading tableElement={tableRef} />}
      </div>
    </>
  );
};

export default GridWidget;
